import type { ProductSelectionItem } from "../Supplier/types/types"

import { US_COUNTRY_ISO_CODE } from "@/constants/locations"
import type { Location } from "@/graphql/codegen/graphql"

/**
 * Finds the source location association ID for Purchase Order from selected products.
 * First tries direct product location associations (only if they belong to the product's
 * current company), then falls back to company location associations.
 *
 * Product location associations can become stale when a product is reassigned to a
 * different company — in that case we skip them and use the company's associations instead.
 *
 * @param selectedProducts - Array of selected products
 * @returns The first available location association ID or null if none found
 */
export const getPOLocationAssociationId = (selectedProducts: ProductSelectionItem[]): string | null => {
  // Try direct product location first, but only if it belongs to the product's current company.
  // This prevents stale product associations (from a previous company) from being used.
  for (const product of selectedProducts) {
    const currentCompanyAssociationIds = new Set(
      product.company?.locationsAssociations?.map((a) => a.companyLocationAssociationId) ?? []
    )
    const locationId = product.locationsAssociations?.find(
      (a) => a.locationAssociationId && currentCompanyAssociationIds.has(a.locationAssociationId)
    )?.locationAssociationId
    if (locationId) return locationId
  }

  // Fallback to company location
  for (const product of selectedProducts) {
    const companyLocationId = product.company?.locationsAssociations?.[0]?.companyLocationAssociationId
    if (companyLocationId) return companyLocationId
  }

  return null
}

export const isLocationOutsideUs = (location?: Location | null): boolean => {
  if (!location) return false
  return location?.country?.isoCode !== US_COUNTRY_ISO_CODE
}
